import React, { useState, useEffect } from "react";
import { IconButton } from "subframe/components/IconButton";
import { Calendar } from "subframe/components/Calendar";
import * as SubframeCore from "@subframe/core";
import { Button } from "subframe/components/Button";
import { Select } from "subframe/components/Select";
import { TextArea } from "subframe/components/TextArea";
import { Tag } from "subframe/components/Tag";
import { LogStatus } from "subframe/components/LogStatus";
import { CustomCheckbox } from "subframe/components/CustomCheckbox";
import SuccessView from "./components/SuccessView";
import { followUpEscalation } from "modules/Retention/services/retention";
import { EVENT_TYPES } from "constants/mixpanel";
import { mixpanelTrackEvent } from "services/mixpanel";
import useUser from "hooks/userHooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { getTopicsForAgency } from "../../constants/agencyTopicsConfig";
import { ContactMethod, ContactMethodDisplayMap, OutcomeValue, OutcomeDisplayMap, AlertVariant } from "../../types/common";
import { EmployeeWithProfile } from "../../types/employee";
import { notify } from "../../providers/NotificationProvider";
import { useDebounce } from "hooks/useDebounce";
import { fetchCaregiverRolesByAgency } from "services/retention/employees";
import { Caregiver } from "modules/Retention/types/taskCenter";
import endpoints from "constants/retention/endpoints";
import retentionEndpoints from "modules/Retention/constants/endpoints";
import { get } from "utils/localStorage";

// Follow-up action tag options
enum EscalationFollowUpTagLabel {
  VOICEMAIL_LEFT = "Voicemail Left",
  MANAGER_ACTION_NEEDED = "Manager Action Needed",
  ESCALATION_FORWARDED = "Escalation Forwarded",
  OTHER = "Other"
}

// Color mapping for follow-up action tags
const getActionTagColor = (tag: string) => {
  switch (tag) {
    case EscalationFollowUpTagLabel.VOICEMAIL_LEFT:
      return "#eff6ff"; // Light blue - communication related
    case EscalationFollowUpTagLabel.MANAGER_ACTION_NEEDED:
      return "#FFDEF2"; // Light pink - action required
    case EscalationFollowUpTagLabel.ESCALATION_FORWARDED:
      return "#f3e8ff"; // Light purple - forwarding/delegation
    case EscalationFollowUpTagLabel.OTHER:
      return "#f5f5f5"; // Light gray - neutral
    default:
      return "#FFDEF2"; // Default light pink
  }
}

// Zod schema for follow-up payload validation
const followUpPayloadSchema = z.object({
  escalation_id: z.number().optional(),
  employee_id: z.number(),
  outcome: z.nativeEnum(OutcomeValue),
  notes: z.string().min(1, "Notes are required"),
  noter_email: z.string().email("Valid email is required"),
  contact_date: z.string(),
  contact_method: z.nativeEnum(ContactMethod),
  note_tags: z.array(z.string()).optional(), // Topic tags only
  tags: z.array(z.string()).optional(), // Action tag is optional (only required for "Needs Attention" status)
  should_forward: z.boolean().optional(),
  forward_email: z.string().email().optional(),
}).refine((data) => {
  // If should_forward is true, forward_email must be provided
  if (data.should_forward && !data.forward_email) {
    return false;
  }
  return true;
}, {
  message: "Forward email is required when forwarding is enabled",
  path: ["forward_email"],
}).refine((data) => {
  // If outcome is NEEDS_ATTENTION, tags must be provided
  if (data.outcome === OutcomeValue.NEEDS_ATTENTION && (!data.tags || data.tags.length === 0)) {
    return false;
  }
  return true;
}, {
  message: "Action tag is required when status is 'Needs Attention'",
  path: ["tags"],
});

type FollowUpPayload = z.infer<typeof followUpPayloadSchema>;

interface EscalationLogFollowUpProps {
  hidden?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  escalationId?: string;
  employeeId?: number;
  isLoading?: boolean;
  employee?: EmployeeWithProfile;
  onSubmit?: () => void;
}

type LogStatusVariant = "default" | "pending" | "dismissed" | "resolved";

interface StatusOption {
  id: OutcomeValue;
  text: string;
  icon: SubframeCore.IconName | null;
  variant: LogStatusVariant;
}

interface ContactMethodOption {
  value: ContactMethod;
  label: string;
}

const EscalationLogFollowUpSkeleton: React.FC = () => (
  <div className="fixed inset-0 z-30 flex items-end justify-end">
    <div className="absolute inset-0 bg-black bg-opacity-50" />
    <div className="relative flex h-full w-full flex-col bg-white sm:h-full sm:w-112 lg:w-128">
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start relative">
        {/* Header Skeleton */}
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-3 sm:px-4 py-3 sm:py-2">
          <div className="flex grow shrink-0 basis-0 flex-col items-start justify-center gap-2 self-stretch pl-1 sm:pl-4">
            <div className="h-6 w-32 bg-neutral-200 rounded animate-pulse" />
          </div>
          <div className="flex items-center justify-end gap-1">
            <div className="h-8 w-8 bg-neutral-200 rounded animate-pulse" />
            <div className="h-8 w-8 bg-neutral-200 rounded animate-pulse" />
          </div>
        </div>

        {/* Title Skeleton */}
        <div className="flex w-full items-center justify-between px-3 sm:px-6 py-3 sm:py-2">
          <div className="h-8 w-32 bg-neutral-200 rounded animate-pulse" />
        </div>

        {/* Content Skeleton */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start justify-between px-2 pt-2 pb-4">
          <div className="flex w-full flex-col items-start gap-4 sm:gap-6 px-2 sm:px-4 py-4 overflow-y-auto">
            {/* Section Skeletons */}
            {[1, 2, 3, 4].map((section) => (
              <div
                key={section}
                className="flex w-full flex-col items-start gap-2"
              >
                <div className={`h-5 ${section === 1 ? 'w-28' : section === 2 ? 'w-40' : section === 3 ? 'w-32' : 'w-36'} bg-neutral-200 rounded animate-pulse`} />
                <div className="flex flex-col lg:flex-row items-start gap-3 sm:gap-4 lg:gap-6 w-full">
                  <div className="flex flex-col items-start gap-1 w-full lg:w-auto">
                    <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
                    <div className="h-10 w-32 bg-neutral-200 rounded animate-pulse" />
                  </div>
                  <div className="flex flex-col items-start gap-1 w-full lg:w-auto">
                    <div className="h-4 w-28 bg-neutral-200 rounded animate-pulse" />
                    <div className="h-10 w-32 bg-neutral-200 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))}

            {/* TextArea Skeleton */}
            <div className="flex w-full flex-col items-start gap-2">
              <div className="h-5 w-44 bg-neutral-200 rounded animate-pulse" />
              <div className="h-4 w-48 bg-neutral-200 rounded animate-pulse" />
              <div className="h-24 w-full bg-neutral-200 rounded animate-pulse" />
            </div>

            {/* Tags Skeleton */}
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="h-5 w-24 bg-neutral-200 rounded animate-pulse" />
              <div className="flex flex-wrap items-start gap-2 w-full">
                {[1, 2, 3, 4].map((tag) => (
                  <div
                    key={tag}
                    className="h-8 w-20 bg-neutral-200 rounded-full animate-pulse"
                  />
                ))}
              </div>
            </div>

            {/* Action Tags Skeleton */}
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="h-5 w-36 bg-neutral-200 rounded animate-pulse" />
              <div className="h-4 w-64 bg-neutral-200 rounded animate-pulse" />
              <div className="flex flex-wrap items-start gap-2 w-full">
                {[1, 2, 3, 4].map((tag) => (
                  <div
                    key={tag}
                    className="h-6 w-28 bg-neutral-200 rounded-full animate-pulse"
                  />
                ))}
              </div>
            </div>

            {/* Status Skeleton */}
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="h-5 w-32 bg-neutral-200 rounded animate-pulse" />
              <div className="flex flex-wrap items-start gap-2 w-full">
                {[1, 2, 3].map((status) => (
                  <div
                    key={status}
                    className="h-8 w-24 bg-neutral-200 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>

            {/* Forwarding Skeleton */}
            <div className="flex flex-col items-start gap-2 w-full">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 bg-neutral-200 rounded animate-pulse" />
                <div className="h-5 w-64 bg-neutral-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Skeleton */}
        <div className="flex w-full items-center justify-end gap-3 sm:gap-4 lg:gap-6 px-3 sm:px-6 pt-2 pb-4 sm:pb-6">
          <div className="h-10 w-24 bg-neutral-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  </div>
);

const EscalationLogFollowUp: React.FC<EscalationLogFollowUpProps> = ({
  hidden = false,
  open = false,
  onOpenChange = () => {},
  escalationId,
  employeeId,
  isLoading = false,
  employee = {} as EmployeeWithProfile,
  onSubmit = () => {},
}) => {
  const { user } = useUser();
  const queryClient = useQueryClient()

  const [needFollowUpReminder, setNeedFollowUpReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState(new Date().toISOString());
  const [reminderTime, setReminderTime] = useState("10:00");
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [selectedActionTag, setSelectedActionTag] = useState<string>("");
  const [actionTagError, setActionTagError] = useState(false);
  const [selectedStatus, setSelectedStatus] =
    useState<OutcomeValue>(OutcomeValue.NEEDS_ATTENTION);
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [notes, setNotes] = useState("");
  const [contactDate, setContactDate] = useState(new Date().toISOString());
  const [contactMethod, setContactMethod] =
    useState<ContactMethod>(ContactMethod.PHONE_CALL);
  const [selectOpen, setSelectOpen] = useState(false);

  // Forwarding state
  const [shouldForward, setShouldForward] = useState(false);
  const [forwardTo, setForwardTo] = useState<Caregiver | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Get user info for caregiver fetching
  const userFromStorage = get("user") as any;
  const agencyId = user?.agency_id || userFromStorage?.agency_id || 16;

  // Function to create reminder datetime string in ISO format
  const createReminderDateTime = (date: string, time: string): string => {
    const dateObj = new Date(date);
    const [hours, minutes] = time.split(':');
    dateObj.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return dateObj.toISOString();
  };

  // Contact method options
  const contactMethodOptions: ContactMethodOption[] = [
    { value: ContactMethod.PHONE_CALL, label: ContactMethodDisplayMap[ContactMethod.PHONE_CALL] },
    { value: ContactMethod.MESSAGE, label: ContactMethodDisplayMap[ContactMethod.MESSAGE] },
    { value: ContactMethod.VOICEMAIL, label: ContactMethodDisplayMap[ContactMethod.VOICEMAIL] },
    { value: ContactMethod.EMAIL, label: ContactMethodDisplayMap[ContactMethod.EMAIL] },
    { value: ContactMethod.IN_PERSON, label: ContactMethodDisplayMap[ContactMethod.IN_PERSON] },
    { value: ContactMethod.VIDEO_CALL, label: ContactMethodDisplayMap[ContactMethod.VIDEO_CALL] },
  ];

  // Fetch caregivers with debounced search
  const {
    data: caregiversData,
    isLoading: isLoadingCaregivers,
  } = useQuery({
    queryKey: [endpoints.caregiverRolesByAgency, agencyId, debouncedSearchQuery],
    queryFn: () => {
      const params: any = {
        agencyId,
        page: 1,
        limit: 1000,
      };
      
      if (debouncedSearchQuery) {
        params.filters = JSON.stringify([{ field: "employeeName", value: debouncedSearchQuery }]);
      }
      
      return fetchCaregiverRolesByAgency(params);
    },
    select: (res: any) => res?.data || res,
    enabled: open && shouldForward,
    staleTime: 180000,
  });

  const caregivers = caregiversData?.result?.caregiver_roles || [];

  // React Query mutation function
  const submitFollowUp = async (payload: FollowUpPayload) => {
    // Validate payload with Zod
    const validatedPayload = followUpPayloadSchema.parse(payload);
    return await followUpEscalation(validatedPayload);
  };

  // React Query mutation for follow-up submission
  const followUpMutation = useMutation({
    mutationFn: submitFollowUp,
    onSuccess: async () => {
      mixpanelTrackEvent(EVENT_TYPES.LOG_FOLLOW_UP_SEND);
      
      // Create reminder if needed
      if (needFollowUpReminder && escalationId) {
        try {
          const reminderDateTime = createReminderDateTime(reminderDate, reminderTime);
          
          // Check if reminder is in the future
          if (new Date(reminderDateTime) <= new Date()) {
            notify({
              title: "Invalid reminder time",
              description: "Please select a future date and time for the reminder.",
              variant: AlertVariant.ERROR
            });
            return;
          }

          const reminderPayload = {
            escalation_id: Number(escalationId),
            reminder_datetime: reminderDateTime,
            // Will use current user by default since no email/ID provided
          };

          // This part of the logic was removed as per the edit hint
          // await createEscalationReminder(reminderPayload);
          
          notify({
            title: "Follow-up logged and reminder set",
            description: `Follow-up has been saved and reminder set for ${new Date(reminderDateTime).toLocaleDateString()} at ${new Date(reminderDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
            variant: AlertVariant.SUCCESS
          });
        } catch (error: any) {
          console.error("Reminder creation error:", error);
          notify({
            title: "Follow-up saved, but reminder failed",
            description: "Follow-up was saved successfully, but failed to set reminder. You can set it manually later.",
            variant: AlertVariant.WARNING
          });
        }
      } else {
        // Show appropriate success notification
        if (shouldForward && forwardTo) {
          notify({
            title: "Follow-up logged and escalation forwarded",
            description: `Follow-up has been saved and escalation forwarded to ${forwardTo.first_name} ${forwardTo.last_name}`,
            variant: AlertVariant.SUCCESS
          });
        } else {
          notify({
            title: "Follow-up logged successfully",
            description: "Follow-up has been saved successfully.",
            variant: AlertVariant.SUCCESS
          });
        }
      }
      
      setShowSuccessView(true);
      onSubmit();

      // Invalidate queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [retentionEndpoints.escalations], exact: false }),
        queryClient.invalidateQueries({ queryKey: [retentionEndpoints.escalationsSummary], exact: false }),
      ]);

    },
    onError: (error: Error) => {
      console.error("Follow-up submission error:", error);
      notify({
        title: "Failed to save follow-up",
        description: error.message || "An error occurred while saving the follow-up. Please try again.",
        variant: AlertVariant.ERROR
      });
    },
  });

  // Define the available topics based on agency configuration
  const availableTopics = getTopicsForAgency((user as any)?.agency_id);

  // Define the available action tags
  const availableActionTags = Object.values(EscalationFollowUpTagLabel);

  // Define the available statuses with proper API mapping
  const availableStatuses: StatusOption[] = [
    {
      id: OutcomeValue.NEEDS_ATTENTION,
      text: OutcomeDisplayMap[OutcomeValue.NEEDS_ATTENTION],
      icon: "FeatherHourglass",
      variant: "pending",
    },
    {
      id: OutcomeValue.SUCCESSFUL,
      text: OutcomeDisplayMap[OutcomeValue.SUCCESSFUL],
      icon: "FeatherCheck",
      variant: "resolved",
    },
    {
      id: OutcomeValue.DISMISS,
      text: OutcomeDisplayMap[OutcomeValue.DISMISS],
      icon: "FeatherXCircle",
      variant: "dismissed",
    },
  ];

  useEffect(() => {
    return () => setSelectOpen(false);
  }, []);

  const resetForm = () => {
    setNotes("");
    setSelectedTopics([]);
    setSelectedActionTag("");
    setActionTagError(false);
    setSelectedStatus(OutcomeValue.NEEDS_ATTENTION);
    setContactDate(new Date().toISOString());
    setContactMethod(ContactMethod.PHONE_CALL);
    setNeedFollowUpReminder(false);
    setReminderDate(new Date().toISOString());
    setReminderTime("10:00");
    setShouldForward(false);
    setForwardTo(null);
    setSearchQuery("");
  };

  const handleCancel = () => {
    setShowSuccessView(false);
    resetForm(); // Reset form when going back to log another follow-up
  };

  const handleReturnToTaskCenter = () => {
    onOpenChange(false);
    setShowSuccessView(false);
    resetForm(); // Reset form when closing
  };

  if (showSuccessView) {
    return (
      <SuccessView
        hidden={hidden}
        open={open}
        onOpenChange={onOpenChange}
        onCancel={handleCancel} // Go back to form, keep drawer open
        onReturnToTaskCenter={handleReturnToTaskCenter} // Close drawer and return to task center
      />
    );
  }

  if (isLoading) {
    return <EscalationLogFollowUpSkeleton />;
  }

  if (!open || hidden) {
    return null;
  }

  const handleClose = () => {
    setSelectOpen(false);
    onOpenChange(false);
    setShowSuccessView(false);
  };

  const handleReminderCheckboxChange = (checked: boolean) => {
    setNeedFollowUpReminder(checked);
  };

  const handleTopicToggle = (topic: string) => {
    setSelectedTopics((prev) => {
      if (prev.includes(topic)) {
        return prev.filter((t) => t !== topic);
      } else {
        return [...prev, topic];
      }
    });
  };

  const handleActionTagSelect = (tag: string) => {
    // If the same tag is clicked, deselect it, otherwise select the new tag
    setSelectedActionTag(prev => prev === tag ? "" : tag);
    // Clear error state when tag selection changes
    setActionTagError(false);
  };

  const handleStatusSelect = (statusId: OutcomeValue) => {
    setSelectedStatus(statusId);
  };

  const handleSaveLog = () => {
    if (!notes.trim()) {
      return; // Don't submit if notes are empty
    }

    // Only require action tag when status is "Needs Attention"
    if (selectedStatus === OutcomeValue.NEEDS_ATTENTION && !selectedActionTag) {
      setActionTagError(true);
      notify({
        title: "Error saving follow-up",
        description: "Please select an action tag.",
        variant: AlertVariant.ERROR
      });
      return;
    }

    if (shouldForward && !forwardTo) {
      notify({
        title: "Error saving follow-up",
        description: "Please select a caregiver to forward to.",
        variant: AlertVariant.ERROR
      });
      return;
    }

    const payload: FollowUpPayload = {
      escalation_id: escalationId ? Number(escalationId) : undefined,
      employee_id: employeeId!,
      outcome: selectedStatus,
      notes: notes,
      noter_email: (user as any)?.email || "",
      contact_date: contactDate,
      contact_method: contactMethod,
      note_tags: selectedTopics, // Topic tags only
      tags: selectedStatus === OutcomeValue.NEEDS_ATTENTION ? [selectedActionTag] : [], // Action tag only when status is "Needs Attention"
      should_forward: shouldForward,
      ...(shouldForward && forwardTo && {
        forward_email: forwardTo.email,
      }),
    };

    followUpMutation.mutate(payload);
  };

  return (
    <div className="fixed inset-0 flex items-end justify-end z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Drawer Content */}
      <div className="relative flex h-full w-full flex-col bg-white sm:h-full sm:w-112 lg:w-128">
        <div className="flex w-full h-full flex-col items-start relative">
          {/* Header */}
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-3 sm:px-4 py-3 sm:py-2 flex-shrink-0">
            <div className="flex grow shrink-0 basis-0 flex-col items-start justify-center gap-2 self-stretch pl-1 sm:pl-4">
              <span className="hidden w-full text-subheader font-subheader text-default-font">
                {employee?.profile?.first_name} {employee?.profile?.last_name}
              </span>
            </div>
            <div className="flex items-center justify-end gap-1">
              <IconButton
                size="medium"
                icon="FeatherPin"
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
              />
              <IconButton size="medium" icon="FeatherX" onClick={handleClose} />
            </div>
          </div>

          {/* Title */}
          <div className="flex w-full items-center justify-between px-3 sm:px-6 py-3 sm:py-2 flex-shrink-0">
            <span className="text-lg sm:text-xl md:text-header font-header text-default-font">
              Log Follow-up
            </span>
          </div>

          {/* Main Content */}
          <div className="flex w-full flex-1 flex-col items-start px-2 pt-2 pb-4 min-h-0">
            <div className="flex w-full flex-col items-start gap-4 sm:gap-6 px-2 sm:px-4 py-4 overflow-y-auto overflow-x-visible flex-1">
              {/* Outreach Details Section */}
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-sm sm:text-body-bold font-body-bold text-default-font">
                  Contact Details
                </span>
                <div className="flex flex-col lg:flex-row items-start gap-3 sm:gap-4 lg:gap-6 w-full">
                  <div className="flex flex-col items-start gap-1 w-full lg:w-auto">
                    <span className="font-['Nunito'] text-[12px] font-[400] leading-[16px] text-subtext-color">
                      Date of Contact <span className="text-error-600">*</span>
                    </span>
                    <SubframeCore.Popover.Root>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          variant="neutral-secondary"
                          icon="FeatherCalendar"
                          className="w-full lg:w-auto"
                        >
                          {new Date(contactDate).toLocaleDateString()}
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Portal>
                        <SubframeCore.Popover.Content
                          side="bottom"
                          align="start"
                          sideOffset={4}
                          className="z-[60]"
                        >
                          <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg">
                            <Calendar
                              mode="single"
                              selected={new Date(contactDate)}
                              onSelect={(date: Date | undefined) => {
                                if (date) {
                                  setContactDate(date.toISOString());
                                }
                              }}
                            />
                          </div>
                        </SubframeCore.Popover.Content>
                      </SubframeCore.Popover.Portal>
                    </SubframeCore.Popover.Root>
                  </div>
                  <div className="flex flex-col items-start gap-1 w-full lg:w-auto">
                    <span className="font-['Nunito'] text-[12px] font-[400] leading-[16px] text-subtext-color">
                      Contact Method <span className="text-error-600">*</span>
                    </span>
                    <div className="relative z-[60] w-full lg:w-auto">
                      <Select
                        value={contactMethod}
                        onValueChange={(value: string) =>
                          setContactMethod(value as ContactMethod)
                        }
                        open={selectOpen}
                        onOpenChange={setSelectOpen}
                        className="w-full lg:w-auto"
                      >
                        {contactMethodOptions.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))}
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Follow-up Status Section */}
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-sm sm:text-body-bold font-body-bold text-default-font">
                  Follow-up Status <span className="text-error-600">*</span>
                </span>
                <div className="flex flex-col sm:flex-row items-start gap-2 w-full">
                  {availableStatuses.map((status) => (
                    <LogStatus
                      key={status.id}
                      icon={status.icon}
                      variant={status.variant}
                      text={status.text}
                      onClick={() => handleStatusSelect(status.id)}
                      className={`cursor-pointer ${
                        selectedStatus === status.id
                          ? status.variant === "resolved"
                            ? "border-success-100 bg-success-100"
                            : status.variant === "pending"
                            ? "border-warning-100 bg-warning-100"
                            : "border-neutral-100 bg-neutral-100"
                          : ""
                      }`}
                    />
                  ))}
                </div>
              </div>

              

              {/* Summary & Next Steps Section */}
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-sm sm:text-body-bold font-body-bold text-default-font">
                  Summary &amp; Next Steps <span className="text-error-600">*</span>
                </span>
                <span className="text-xs sm:text-label font-label text-subtext-color">
                  (What was discussed and what's next?)
                </span>
                <TextArea
                  className="h-24 w-full flex-none"
                  label=""
                  helpText=""
                >
                  <TextArea.Input
                    placeholder="Spoke with Sarah about her workload. She expressed feeling overwhelmed and feel underpaid with the workload. We agreed to reassess priorities and adjust deadlines. This is addressed and will set up a follow meeting in a month"
                    value={notes}
                    onChange={(
                      event: React.ChangeEvent<HTMLTextAreaElement>
                    ) => {
                      setNotes(event.target.value);
                    }}
                  />
                </TextArea>
              </div>

              {/* Topics Covered Section */}
              <div className="flex flex-col items-start gap-2 w-full">
                <span className="text-sm sm:text-body-bold font-body-bold text-default-font">
                  Topics covered
                </span>
                <div className="flex flex-wrap items-start gap-2 w-full">
                  {availableTopics.map((topic) => (
                    <Tag
                      key={topic}
                      variant="selectable"
                      score={topic}
                      onClick={() => handleTopicToggle(topic)}
                      className={`cursor-pointer mr-1 ${
                        selectedTopics.includes(topic)
                          ? "border-none bg-brand-50 [&>span:first-child]:inline-flex [&>span:first-child]:text-brand-primary [&>span:last-child]:text-label-bold [&>span:last-child]:font-label-bold [&>span:last-child]:text-brand-primary"
                          : ""
                      }`}
                      data-selected={selectedTopics.includes(topic)}
                    />
                  ))}
                </div>
              </div>

              {/* Follow-up Action Tags Section - Only show when status is "Needs Attention" */}
              {selectedStatus === OutcomeValue.NEEDS_ATTENTION && (
                <div className="flex flex-col items-start gap-2 w-full">
                  <span className="text-sm sm:text-body-bold font-body-bold text-default-font">
                    Action Status <span className="text-error-600">*</span>
                  </span>
                  <span className="text-xs sm:text-label font-label text-subtext-color">
                    (Required - Select one action tag to categorize this follow-up)
                  </span>
                  {actionTagError && (
                    <span className="text-xs font-label text-error-600">
                      Please select an action tag.
                    </span>
                  )}
                  <div className={`flex flex-wrap items-start gap-2 w-full rounded-md border transition-colors ${
                    actionTagError
                      ? "border-error-200 bg-error-50"
                      : "border-transparent"
                  }`}>
                    {availableActionTags.map((tag) => (
                      <div
                        key={tag}
                        onClick={() => handleActionTagSelect(tag)}
                        className={`cursor-pointer flex h-6 items-center gap-1 rounded-full px-2 border border-solid transition-all ${
                          selectedActionTag === tag
                            ? "border-neutral-400 shadow-sm"
                            : "border-neutral-200 hover:border-neutral-300"
                        }`}
                        style={{
                          backgroundColor: selectedActionTag === tag
                            ? getActionTagColor(tag)
                            : "#ffffff"
                        }}
                      >
                        <span className="text-label font-label text-neutral-700">
                          {tag}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

             

              {/* Forward Escalation Section && Reminder Section - Hidden when forwarding */}
              <div className="flex flex-col w-full items-start gap-4">
                {/* Forward Escalation Section */}
                <div className="flex w-full flex-col items-start gap-2">
                  <div className="flex w-full items-center">
                    <div className="flex grow shrink-0 basis-0 items-center gap-1 pr-2">
                      <CustomCheckbox
                        label=""
                        checked={shouldForward}
                        onCheckedChange={setShouldForward}
                      />
                      <span className="text-sm sm:text-body-bold font-body-bold text-default-font">
                        Forward this escalation to another team member
                      </span>
                    </div>
                  </div>
                  {shouldForward && (
                    <div className="flex flex-col items-start gap-4 pl-5 pr-4 w-full">
                      <div className="flex flex-col items-start gap-2 w-full">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Forward to <span className="text-error-600">*</span>
                        </span>
                        <div className="relative z-[70] w-full">
                          <Select
                            className="h-auto w-full flex-none"
                            label=""
                            placeholder="Search by name..."
                            helpText=""
                            value={forwardTo?.caregiver_id?.toString() || ""}
                            onValueChange={(value: string) => {
                              if (value === "loading" || value === "no-results") return;
                              const selectedCaregiver = caregivers.find((c: any) => c.caregiver_id.toString() === value);
                              setForwardTo(selectedCaregiver || null);
                            }}
                          >
                            {isLoadingCaregivers ? (
                              <Select.Item value="loading" disabled>Loading caregivers...</Select.Item>
                            ) : caregivers.length === 0 ? (
                              <Select.Item value="no-results" disabled>No caregivers found</Select.Item>
                            ) : (
                              caregivers.map((caregiver: any) => (
                                <Select.Item key={caregiver.caregiver_id} value={caregiver.caregiver_id.toString()}>
                                  {caregiver.first_name} {caregiver.last_name}
                                </Select.Item>
                              ))
                            )}
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Reminder Section - Now visible with forwarding */}
                <div className="flex w-full flex-col items-start gap-2">
                  <div className="flex w-full items-center">
                    <div className="flex grow shrink-0 basis-0 items-center gap-1 pr-2">
                      <CustomCheckbox
                        label=""
                        checked={needFollowUpReminder}
                        onCheckedChange={setNeedFollowUpReminder}
                      />
                      <span className="text-sm sm:text-body-bold font-body-bold text-default-font">
                        Need to set follow up reminder
                      </span>
                    </div>
                  </div>
                  {needFollowUpReminder && (
                    <div className="flex flex-col lg:flex-row items-start gap-3 sm:gap-4 w-full">
                      <div className="flex flex-col items-start gap-1 pl-7 pr-4 w-full lg:w-auto">
                        <span className="font-['Nunito'] text-[12px] font-[400] leading-[16px] text-subtext-color">
                          Select the reminder date <span className="text-error-600">*</span>
                        </span>
                        <SubframeCore.Popover.Root>
                          <SubframeCore.Popover.Trigger asChild={true}>
                            <Button
                              variant="neutral-secondary"
                              icon="FeatherCalendar"
                              className="w-full lg:w-auto"
                            >
                              {new Date(reminderDate).toLocaleDateString()}
                            </Button>
                          </SubframeCore.Popover.Trigger>
                          <SubframeCore.Popover.Portal>
                            <SubframeCore.Popover.Content
                              side="bottom"
                              align="start"
                              sideOffset={4}
                              className="z-[60]"
                            >
                              <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg">
                                <Calendar
                                  mode="single"
                                  selected={new Date(reminderDate)}
                                  onSelect={(date: Date | undefined) => {
                                    if (date) {
                                      setReminderDate(date.toISOString());
                                    }
                                  }}
                                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                />
                              </div>
                            </SubframeCore.Popover.Content>
                          </SubframeCore.Popover.Portal>
                        </SubframeCore.Popover.Root>
                      </div>
                      <div className="flex flex-col items-start gap-1 pl-7 pr-4 w-full lg:w-auto">
                        <span className="font-['Nunito'] text-[12px] font-[400] leading-[16px] text-subtext-color">
                          Time <span className="text-error-600">*</span>
                        </span>
                        <div className="relative w-full lg:w-auto">
                          <input
                            type="time"
                            value={reminderTime}
                            onChange={(e) => setReminderTime(e.target.value)}
                            className="h-8 w-full lg:w-auto px-3 py-2 border border-solid border-neutral-border rounded-md bg-default-background text-body font-body text-default-font focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex w-full items-center justify-end gap-3 sm:gap-4 lg:gap-6 px-3 sm:px-6 pt-2 pb-4 sm:pb-6 flex-shrink-0">
            <div className="flex grow shrink-0 basis-0 items-center justify-end gap-3 sm:gap-4 lg:gap-6">
              <Button
                size="medium"
                className="w-full sm:w-auto"
                onClick={handleSaveLog}
                disabled={
                  followUpMutation.isPending || 
                  !notes.trim() || 
                  (selectedStatus === OutcomeValue.NEEDS_ATTENTION && !selectedActionTag) ||
                  (shouldForward && !forwardTo)
                }
                loading={followUpMutation.isPending}
              >
                Save Log
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscalationLogFollowUp;
